import { useAtomValue } from 'jotai'
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'

import iconNotificationSettingsSrc from '@cowprotocol/assets/images/icon-notification-settings.svg'
import { useMediaQuery, useOnClickOutside } from '@cowprotocol/common-hooks'
import { Media } from '@cowprotocol/ui'

import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { createPortal } from 'react-dom'
import SVG from 'react-inlinesvg'

import { CowSwapAnalyticsCategory, toCowSwapGtmEvent } from 'common/analytics/types'
import { openModalState } from 'common/state/openModalState'

import {
  Sidebar,
  SidebarHeader,
  ArrowLeft,
  NotificationSettingsIcon,
  EnableAlertsButtonWithIcon,
} from './NotificationSidebar.styled'

import { useHasNotificationSubscription } from '../../hooks/useHasNotificationSubscription'
import { useNotificationSettingsPopoverDismissal } from '../../hooks/useNotificationSettingsPopoverDismissal'
import { useTelegramNotificationsAvailability } from '../../hooks/useTelegramNotificationsAvailability'
import { NotificationSettingsPopover } from '../../pure/NotificationSettingsPopover'
import { NotificationSettings } from '../NotificationSettings'
import { NotificationsList } from '../NotificationsList'

interface NotificationsHeaderProps {
  isMobile: boolean
  areTelegramNotificationsAvailable: boolean
  hasSubscription: boolean
  onDismiss: () => void
  onToggleSettings: () => void
  onEnableAlerts: () => void
  shouldShowSettingsPopover: boolean
  onDismissSettingsPopover: () => void
  headerRef: React.RefObject<HTMLDivElement | null>
  settingsPopoverContentRef: React.RefObject<HTMLDivElement | null>
}

interface NotificationSidebarProps {
  isOpen: boolean
  onClose: () => void
  initialSettingsOpen?: boolean
}

interface SettingsHeaderProps {
  onBack: () => void
}

export function NotificationSidebar({
  isOpen,
  onClose,
  initialSettingsOpen = false,
}: NotificationSidebarProps): ReactNode {
  const [isSettingsOpen, setIsSettingsOpen] = useState(initialSettingsOpen)
  const sidebarRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const settingsPopoverContentRef = useRef<HTMLDivElement>(null)
  const isMobile = useMediaQuery(Media.upToSmall(false))
  const isAnyModalOpen = useAtomValue(openModalState)

  const { isAvailable: areTelegramNotificationsAvailable, isUnsupportedChain } = useTelegramNotificationsAvailability()
  const { hasSubscription } = useHasNotificationSubscription()
  const { isDismissed: isSettingsPopoverDismissed, dismiss: dismissSettingsPopover } =
    useNotificationSettingsPopoverDismissal()

  // Show settings popover when trade alerts are enabled (hasSubscription) but popover hasn't been dismissed
  const shouldShowSettingsPopover = hasSubscription && !isSettingsPopoverDismissed && !isSettingsOpen

  // Sync state when initialSettingsOpen prop changes
  useEffect(() => {
    setIsSettingsOpen(initialSettingsOpen)
  }, [initialSettingsOpen])

  useEffect(() => {
    if (!areTelegramNotificationsAvailable) {
      setIsSettingsOpen(false)
    }
  }, [areTelegramNotificationsAvailable])

  const onDismiss = useCallback(() => {
    onClose()
    setIsSettingsOpen(false)
  }, [onClose])

  // Don't dismiss the sidebar on outside clicks while a modal (e.g. the Telegram
  // connect modal) is open - it portals outside `sidebarRef`'s DOM subtree, so any
  // click inside it would otherwise be treated as "outside the sidebar" and close it.
  // The settings popover content is portaled the same way, so it's excluded explicitly
  // via `settingsPopoverContentRef` rather than being gated behind a boolean.
  useOnClickOutside([sidebarRef, settingsPopoverContentRef], isAnyModalOpen ? undefined : onDismiss)

  const toggleSettingsOpen = useCallback(() => {
    setIsSettingsOpen((prev) => !prev)
  }, [])

  const handleEnableAlertsClick = useCallback(() => {
    toggleSettingsOpen()
  }, [toggleSettingsOpen])

  if (!isOpen) return null

  // Force re-mount of NotificationsList when returning from settings to refresh subscription status
  const listKey = `notifications-list-${isSettingsOpen}`

  const notificationSidebarElement = (
    <Sidebar ref={sidebarRef} isOpen={isOpen}>
      {isSettingsOpen && areTelegramNotificationsAvailable ? (
        <NotificationSettings isSettingsOpen={isSettingsOpen}>
          <SettingsHeader onBack={toggleSettingsOpen} />
        </NotificationSettings>
      ) : (
        <NotificationsList
          key={listKey}
          hasSubscription={hasSubscription}
          onToggleSettings={areTelegramNotificationsAvailable ? toggleSettingsOpen : undefined}
          isUnsupportedChain={isUnsupportedChain}
        >
          <NotificationsHeader
            isMobile={isMobile}
            areTelegramNotificationsAvailable={areTelegramNotificationsAvailable}
            hasSubscription={hasSubscription}
            onDismiss={onDismiss}
            onToggleSettings={toggleSettingsOpen}
            onEnableAlerts={handleEnableAlertsClick}
            shouldShowSettingsPopover={shouldShowSettingsPopover}
            onDismissSettingsPopover={dismissSettingsPopover}
            headerRef={headerRef}
            settingsPopoverContentRef={settingsPopoverContentRef}
          />
        </NotificationsList>
      )}
    </Sidebar>
  )

  const portalTarget = typeof document !== 'undefined' ? document.body : null

  return portalTarget ? createPortal(notificationSidebarElement, portalTarget) : notificationSidebarElement
}

function NotificationsHeader({
  isMobile,
  areTelegramNotificationsAvailable,
  hasSubscription,
  onDismiss,
  onToggleSettings,
  onEnableAlerts,
  shouldShowSettingsPopover,
  onDismissSettingsPopover,
  headerRef,
  settingsPopoverContentRef,
}: NotificationsHeaderProps): ReactNode {
  return (
    <SidebarHeader ref={headerRef}>
      <span>
        <ArrowLeft
          onClick={onDismiss}
          data-click-event={toCowSwapGtmEvent({
            category: CowSwapAnalyticsCategory.NOTIFICATIONS,
            action: 'Close notifications panel',
            label: isMobile ? 'mobile' : 'desktop',
          })}
        />
      </span>
      <h3>
        <Trans>Notifications</Trans>
      </h3>
      {areTelegramNotificationsAvailable &&
        (hasSubscription ? (
          <NotificationSettingsPopover
            show={shouldShowSettingsPopover}
            onDismiss={onDismissSettingsPopover}
            containerRef={headerRef}
            contentRef={settingsPopoverContentRef}
          >
            <NotificationSettingsIcon
              onClick={onToggleSettings}
              aria-label={t`Trade alert settings`}
              data-click-event={toCowSwapGtmEvent({
                category: CowSwapAnalyticsCategory.NOTIFICATIONS,
                action: 'Open notification settings',
                label: 'notification sidebar',
              })}
            >
              <SVG src={iconNotificationSettingsSrc} />
            </NotificationSettingsIcon>
          </NotificationSettingsPopover>
        ) : (
          <EnableAlertsButtonWithIcon
            onClick={onEnableAlerts}
            data-click-event={toCowSwapGtmEvent({
              category: CowSwapAnalyticsCategory.NOTIFICATIONS,
              action: 'Enable trade alerts',
              label: 'notification sidebar',
            })}
          />
        ))}
    </SidebarHeader>
  )
}

function SettingsHeader({ onBack }: SettingsHeaderProps): ReactNode {
  return (
    <SidebarHeader>
      <span>
        <ArrowLeft
          onClick={onBack}
          data-click-event={toCowSwapGtmEvent({
            category: CowSwapAnalyticsCategory.NOTIFICATIONS,
            action: 'Close notification settings',
          })}
        />
      </span>
      <h3>
        <Trans>Trade alerts</Trans>
      </h3>
    </SidebarHeader>
  )
}
